/* Mix & Match bundle builder for the pdp-bundle template.
 *
 * Cart contract: one paid pack variant carries the bundle price. Selected
 * shapes are added as $0 component lines for fulfillment and are also recorded
 * on the parent as readable line-item properties.
 */
(function () {
  const COMPONENT_BY_SKU = {
    'spaghetti-lc-1ct-shopify': 53960650621247,
    'spaghetti-k-1ct-shopify': 53960651604287,
    'penne-lc-1ct-shopify': 53960651505983,
    'rotini-lc-1ct-shopify': 53960651538751,
    'elbows-lc-1ct-shopify': 53960651571519,
    'fettuccine-lc-1ct-shopify': 53960651637055,
    'linguine-lc-1ct-shopify': 53960651669823,
    'gnocchi-keto-1ct-shopify': 53960651702591,
    'gnocchi-pro-1ct-shopify': 53960651735359,
    'gnocchi-lite-1ct-shopify': 53960651768127,
    'lasagna-lc-1ct-shopify': 53960651800895,
  };

  // The picker uses unchanged standalone SKUs; fulfillment uses component SKUs.
  const COMPONENT_SKU_BY_VARIANT = {
    '53960650621247': 'KETO-SPA-1',
    '53960651505983': 'KETO-PEN-1',
    '53960651538751': 'KETO-ROT-1',
    '53960651571519': 'KETO-ELB-1',
    '53960651604287': 'UNO-SPA-1',
    '53960651637055': 'KETO-FET-1',
    '53960651669823': 'KETO-LIN-1',
    '53960651702591': 'KETO-GNO-1',
    '53960651735359': 'PRO-GNO-1',
    '53960651768127': 'LITE-GNO-1',
    '53960651800895': 'KETO-LAS-1',
  };

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function discountPercent(comparePrice, price) {
    comparePrice = Number(comparePrice || 0);
    price = Number(price || 0);
    if (!comparePrice || price >= comparePrice) return 0;

    return Math.round(((comparePrice - price) / comparePrice) * 100);
  }

  class PdpBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.packs = JSON.parse(this.querySelector('[data-pdpb-packs]').textContent)
        .filter((pack) => pack.size > 0 && pack.variantId)
        .sort((a, b) => a.size - b.size);
      this.counts = {};
      this.submitting = false;
      this.cartError = '';

      this.items = Array.from(this.querySelectorAll('[data-pdpb-item]'));
      this.items.forEach((item) => {
        const id = item.dataset.variantId;
        item.dataset.componentVariantId = COMPONENT_BY_SKU[(item.dataset.sku || '').trim()] || '';
        item.querySelector('[data-pdpb-add]').addEventListener('click', () => this.change(id, 1));
        item.querySelector('[data-pdpb-plus]').addEventListener('click', () => this.change(id, 1));
        item.querySelector('[data-pdpb-minus]').addEventListener('click', () => this.change(id, -1));
      });

      this.checkoutButton = this.querySelector('[data-pdpb-checkout]');
      this.checkoutButton.addEventListener('click', () => this.addToCart());

      /* Subscribe & Save: plan group differs per pack (4-pack 5%, 6/8/12 12%),
         so the frequency list is rebuilt whenever the target pack changes. */
      const initialPurchase = this.querySelector('[data-pdpb-purchase]:checked');
      this.isSubscription = Boolean(initialPurchase && initialPurchase.value === 'subscribe');
      this.planIndex = 0;
      this.renderedPlansFor = null;
      this.planSelect = this.querySelector('[data-pdpb-plan-select]');
      this.querySelectorAll('[data-pdpb-purchase]').forEach((input) => {
        input.addEventListener('change', () => {
          this.isSubscription = input.value === 'subscribe' && input.checked;
          this.render();
        });
      });
      if (this.planSelect) {
        this.planSelect.addEventListener('change', () => {
          this.planIndex = this.planSelect.selectedIndex;
          this.render();
        });
      }

      this.render();
      this.refreshAvailability();
    }

    get selectedPlan() {
      const plans = this.targetPack.plans || [];
      return plans[this.planIndex] || plans[0] || null;
    }

    /* Price is carried by the pack "container" variant (compareAt -> oneTimePrice),
       mirroring the live /products/bundle-builder-* pages. Subscription stacks the
       Stay.AI selling-plan % on top of the already-discounted one-time price. */
    get oneTimeTotal() {
      const pack = Number(this.targetPack.oneTimePrice) || 0;
      return pack || this.selectionTotal();
    }

    subscriptionTotal(percent) {
      return Math.round(this.oneTimeTotal * (1 - (percent || 0) / 100));
    }

    displayTotal() {
      const plan = this.isSubscription ? this.selectedPlan : null;
      if (!plan) return this.oneTimeTotal;
      return Number(plan.price) || this.subscriptionTotal(plan.percent);
    }

    syncPlans() {
      if (!this.planSelect) return;
      const target = this.targetPack;
      if (this.renderedPlansFor === target.size) return;
      this.renderedPlansFor = target.size;
      const keepIndex = Math.min(this.planIndex, Math.max(0, (target.plans || []).length - 1));
      this.planSelect.innerHTML = '';
      (target.plans || []).forEach((plan) => {
        const option = document.createElement('option');
        option.value = plan.id;
        option.textContent = plan.label;
        this.planSelect.appendChild(option);
      });
      this.planSelect.selectedIndex = keepIndex;
      this.planIndex = keepIndex;
      const badge = this.querySelector('[data-pdpb-sub-badge]');
      if (badge && this.selectedPlan && this.selectedPlan.percent) {
        badge.textContent = 'Save ' + this.selectedPlan.percent + '%';
      }
    }

    get count() {
      return Object.values(this.counts).reduce((sum, n) => sum + n, 0);
    }

    get maxSize() {
      return this.packs[this.packs.length - 1].size;
    }

    /* smallest pack that fits the current count — the "jump to the next pack" rule */
    get targetPack() {
      return this.packs.find((pack) => this.count <= pack.size) || this.packs[this.packs.length - 1];
    }

    get isExactPack() {
      return this.packs.some((pack) => pack.size === this.count);
    }

    change(variantId, delta) {
      const item = this.items.find((candidate) => candidate.dataset.variantId === String(variantId));
      if (delta > 0 && item && item.dataset.available === 'false') return;
      const next = (this.counts[variantId] || 0) + delta;
      if (next < 0) return;
      if (delta > 0 && this.count >= this.maxSize) return;
      this.cartError = '';
      this.counts[variantId] = next;
      this.render();
    }

    async readItemAvailability(item) {
      const componentVariantId = item.dataset.componentVariantId;
      if (!componentVariantId) return null;

      try {
        const response = await window.fetch('/variants/' + componentVariantId + '.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) return null;
        const variant = await response.json();
        return variant.available !== false;
      } catch (error) {
        console.warn('[Pete\'s Mix & Match] Availability check failed', error);
        return null;
      }
    }

    setItemAvailability(item, available) {
      if (available === null) return;
      const variantId = item.dataset.variantId;
      item.dataset.available = String(available);
      item.classList.toggle('is-unavailable', !available);
      item.setAttribute('aria-disabled', String(!available));

      const add = item.querySelector('[data-pdpb-add]');
      add.disabled = !available;
      add.textContent = available ? 'Add +' : 'Sold out';

      if (!available && this.counts[variantId]) {
        delete this.counts[variantId];
      }
    }

    async refreshAvailability(items = this.items) {
      const results = await Promise.all(
        items.map(async (item) => ({ item, available: await this.readItemAvailability(item) }))
      );
      results.forEach(({ item, available }) => this.setItemAvailability(item, available));
      this.render();
      return results.filter(({ available }) => available === false).map(({ item }) => item);
    }

    friendlyCartError(message) {
      const soldOut = String(message || '').match(/The product '(.+?)(?: \(Bundle Component\))?' is already sold out\.?/i);
      if (soldOut) {
        return soldOut[1] + ' is temporarily unavailable for Mix & Match. Please remove it and try again.';
      }

      return message || 'We could not add this bundle. Please try again or contact us for help.';
    }

    selectionTotal() {
      return this.items.reduce((sum, item) => {
        return sum + (this.counts[item.dataset.variantId] || 0) * Number(item.dataset.price);
      }, 0);
    }

    /* Mirror the reference page: show the discounted one-time price and the
       further-discounted subscription price on the purchase rows, and swap the
       tier badges between the one-time (15/15/18/20) and stacked subscription
       (25/25/28/30) ladders depending on the selected purchase type. */
    updatePurchaseDisplay() {
      const target = this.targetPack;
      const compareAt = Number(target.compareAt) || 0;
      const oneTime = this.oneTimeTotal;
      const plan = this.selectedPlan;
      const subPrice = Number(plan && plan.price) || this.subscriptionTotal(plan && plan.percent);
      const renewalPrice = Number(plan && plan.renewalPrice) || subPrice;
      const subscriptionDiscount = discountPercent(compareAt, subPrice);
      const renewalDiscount = discountPercent(compareAt, renewalPrice);

      this.classList.toggle('is-subscription', this.isSubscription);

      const setRow = (priceSel, compareSel, price) => {
        const priceEl = this.querySelector(priceSel);
        const compareEl = this.querySelector(compareSel);
        if (priceEl) priceEl.textContent = money(price);
        if (compareEl) {
          if (compareAt > price) {
            compareEl.textContent = money(compareAt);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      };
      setRow('[data-pdpb-onetime-price]', '[data-pdpb-onetime-compare]', oneTime);
      setRow('[data-pdpb-sub-price]', '[data-pdpb-sub-compare]', subPrice);

      const subBadge = this.querySelector('[data-pdpb-sub-badge]');
      const badgeDiscount = target.size === 4 ? subscriptionDiscount : subscriptionDiscount;
      if (subBadge) {
        subBadge.hidden = !badgeDiscount;
        if (badgeDiscount) subBadge.textContent = 'Save ' + badgeDiscount + '%';
      }
      this.querySelectorAll('[data-pdpb-subscription-today-discount]').forEach((el) => {
        el.textContent = subscriptionDiscount + '%';
      });
      this.querySelectorAll('[data-pdpb-subscription-renewal-discount]').forEach((el) => {
        el.textContent = renewalDiscount + '%';
      });

      this.querySelectorAll('[data-pdpb-tier]').forEach((tier) => {
        const size = Number(tier.dataset.size);
        const oneTimeEl = tier.querySelector('[data-pdpb-tier-off="onetime"]');
        const subEl = tier.querySelector('[data-pdpb-tier-off="subscribe"]');
        if (size === 4) {
          const four = this.packs.find((pack) => pack.size === 4);
          const firstPlan = four && four.plans && four.plans[0];
          const oneTimeDiscount = discountPercent(four && four.compareAt, four && four.oneTimePrice);
          const firstSubDiscount = discountPercent(four && four.compareAt, firstPlan && firstPlan.price);
          if (oneTimeEl) oneTimeEl.textContent = oneTimeDiscount ? oneTimeDiscount + '% Off' : '';
          if (subEl) subEl.textContent = firstSubDiscount ? firstSubDiscount + '% Off' : '';
          if (oneTimeEl) oneTimeEl.hidden = this.isSubscription || !oneTimeDiscount;
          if (subEl) subEl.hidden = !this.isSubscription || !firstSubDiscount;
        } else {
          if (oneTimeEl) oneTimeEl.hidden = this.isSubscription;
          if (subEl) subEl.hidden = !this.isSubscription;
        }
        const shipping = tier.querySelector('[data-pdpb-tier-ship]');
        if (shipping) shipping.classList.toggle('is-unavailable', !this.isSubscription && size === 4);
      });
    }

    render() {
      const count = this.count;
      const target = this.targetPack;
      this.syncPlans();
      this.updatePurchaseDisplay();

      this.items.forEach((item) => {
        const n = this.counts[item.dataset.variantId] || 0;
        const available = item.dataset.available !== 'false';
        item.classList.toggle('has-qty', n > 0);
        item.querySelector('[data-pdpb-count]').textContent = n;
        item.querySelector('[data-pdpb-add]').hidden = n > 0;
        item.querySelector('[data-pdpb-add]').disabled = !available;
        item.querySelector('.pdp-bundle__builder-stepper').hidden = n === 0;
        item.querySelector('[data-pdpb-plus]').disabled = !available || count >= this.maxSize;
      });

      this.querySelectorAll('[data-pdpb-tier]').forEach((tier) => {
        const size = Number(tier.dataset.size);
        tier.classList.toggle('is-reached', count >= size);
        tier.classList.toggle('is-target', size === target.size && count > 0);
      });

      const status = this.querySelector('[data-pdpb-status]');
      const upsell = this.querySelector('[data-pdpb-upsell]');
      const text = this.querySelector('[data-pdpb-checkout-text]');
      const remaining = target.size - count;

      upsell.hidden = true;
      status.removeAttribute('role');
      if (this.cartError) {
        status.textContent = this.cartError;
        status.setAttribute('role', 'alert');
      } else if (count === 0) {
        status.textContent = 'Add ' + target.size + ' bags to start your bundle';
        text.textContent = 'Add ' + target.size + ' bags';
      } else if (this.isExactPack) {
        status.textContent = count + ' bag ' + target.size + '-pack bundle complete!';
        text.textContent = 'Add to Cart - ' + money(this.displayTotal());
        const next = this.packs[this.packs.indexOf(target) + 1];
        if (next) {
          const extra = next.size - count;
          let offer = this.isSubscription && next.subOff ? next.subOff + '% Off' : next.off || '';
          if (!this.isSubscription && next.ship && !target.ship) offer += ' + Free Shipping';
          upsell.textContent = 'Buy ' + extra + ' more for ' + offer;
          upsell.hidden = false;
        }
      } else {
        status.textContent = count + '/' + target.size + ' bags - add ' + remaining + ' more for the ' + target.size + '-pack';
        text.textContent = 'Add ' + remaining + ' more bag' + (remaining === 1 ? '' : 's');
      }
      this.checkoutButton.disabled = !this.isExactPack || this.submitting;
    }

    async addToCart() {
      if (!this.isExactPack || this.submitting) return;
      this.submitting = true;
      this.cartError = '';
      this.render();

      const selectedItems = this.items.filter((item) => (this.counts[item.dataset.variantId] || 0) > 0);
      const unavailableItems = await this.refreshAvailability(selectedItems);
      if (unavailableItems.length) {
        const names = unavailableItems.map((item) => item.dataset.flavor || 'A selected item');
        this.cartError = names.join(', ') +
          (names.length === 1 ? ' is' : ' are') +
          ' temporarily unavailable for Mix & Match. Please choose another shape.';
        this.submitting = false;
        this.render();
        return;
      }

      const text = this.querySelector('[data-pdpb-checkout-text]');
      const original = text.textContent;
      text.textContent = 'Adding...';

      const bundleId = Math.random().toString(36).slice(2, 11);
      const plan = this.isSubscription ? this.selectedPlan : null;
      const properties = {
        _bundle: bundleId,
        _bundle_main: 'true',
        _bundle_id: bundleId,
        _bundle_parent: true,
        _bundle_min: this.targetPack.size,
        _bundle_max: this.targetPack.size,
      };
      let propertyIndex = 1;

      this.items.forEach((item) => {
        const n = this.counts[item.dataset.variantId] || 0;
        if (n === 0) return;
        const flavor = item.dataset.flavor || 'Pasta';
        properties['Flavor ' + propertyIndex] = n + ' x ' + flavor + ' 1 Pack';
        propertyIndex += 1;
      });

      const items = [
        {
          id: this.targetPack.variantId,
          quantity: 1,
          properties: properties,
        },
      ];
      if (plan) {
        items[0].selling_plan = Number(plan.id);
      }

      /* OMG fix: also add the selected shapes as real $0 component SKU lines
         (fulfillment reads SKUs on line items; Flavor props are display-only). */
      this.items.forEach((item) => {
        const n = this.counts[item.dataset.variantId] || 0;
        if (n === 0) return;
        const comp = COMPONENT_BY_SKU[(item.dataset.sku || '').trim()];
        if (!comp) return;
        const child = {
          id: comp,
          quantity: n,
          properties: { _bundle: bundleId, _bundle_id: bundleId, flavor: item.dataset.flavor || '', sku: COMPONENT_SKU_BY_VARIANT[comp] },
        };
        if (plan) child.selling_plan = Number(plan.id);
        items.push(child);
      });

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(function () { return null; });
          throw new Error((payload && payload.description) || 'Unable to add bundle to cart');
        }
        document.dispatchEvent(new CustomEvent('toggle-cart'));
        window.dispatchEvent(
          new CustomEvent('petes:cart-mutated', {
            detail: { source: 'pdp-bundle', itemCount: items.length },
          })
        );
        if (typeof window.updateCart === 'function') {
          await Promise.resolve(window.updateCart());
        } else {
          window.location.href = '/cart';
        }
      } catch (error) {
        console.error('[Pete\'s Mix & Match]', error);
        this.cartError = this.friendlyCartError(error && error.message);
      } finally {
        this.submitting = false;
        text.textContent = original;
        this.render();
      }
    }
  }

  if (!customElements.get('pdp-bundle-builder')) {
    customElements.define('pdp-bundle-builder', PdpBundleBuilder);
  }
})();
